import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbDateStruct, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import {
  MustMatch,
  MustMatchIfValue,
  validateSingleSpaceEntries,
} from '../../shared/form-validations/custom-validators';
import { PasswordValidation } from '../../shared/form-validations/password-validation';
import { OeAppPersonService } from '../../shared/oe-app-person.service';
import { ProfileService } from '../../shared/profile.service';
import { TokenService } from '../../shared/token.service';
import { TranslationService } from '../../shared/translation.service';
import { LanguageInfoDto } from '../../shared/types/LanguageInfoDto';
import { PersonDataDto } from '../../shared/types/PersonDataDto';
import { AccountType } from '../../shared/types/account-type';

const MINIMUM_AGE = 18;

@Component({
  selector: 'lp2-personal-info',
  templateUrl: './personal-info.component.html',
  styleUrls: ['./personal-info.component.scss'],
})
export class PersonalInfoComponent implements OnInit {
  @ViewChild('oldPasswordTemplate') oldPasswordTemplate: ElementRef;

  ngbDatePickerMaxDate: NgbDateStruct;

  modalRef: NgbModalRef;

  personData: PersonDataDto;

  accountType = AccountType;

  languagesDescriptions: LanguageInfoDto[] = [];

  personalForm: FormGroup;

  isRequesting = false;

  savedDisplayed = false;

  isValidationError = false;

  isError = false;

  constructor(
    private oeAppPersonService: OeAppPersonService,
    private profileService: ProfileService,
    private modalService: NgbModal,
    private translationService: TranslationService,
    private tokenService: TokenService
  ) {
    this.personalForm = new FormGroup({
      firstName: new FormControl('', [Validators.required]),
      lastName: new FormControl('', [Validators.required]),
      gender: new FormControl('', [Validators.required]),
      birthDay: new FormControl(''),
      email: new FormControl('', [Validators.required, Validators.email]),
      confirmEmail: new FormControl('', [Validators.email]),
      password: new FormControl(''),
      confirmPassword: new FormControl(''),
      oldPassword: new FormControl(''),
      language: new FormControl('', [Validators.required]),
    });

    this.translationService.getLanguageDescriptions().subscribe((languages) => {
      this.languagesDescriptions = languages;
    });
    const date = new Date();
    date.setFullYear(date.getFullYear() - MINIMUM_AGE);
    this.ngbDatePickerMaxDate = {
      day: date.getDate(),
      month: date.getMonth() + 1,
      year: date.getFullYear(),
    };
  }

  loadUserData() {
    this.personData = this.oeAppPersonService.getPersonData();

    if (this.personData) {
      let birthDay;
      if (this.personData.dob) {
        const dobArr = this.personData.dob.split('-');
        birthDay = new Date(+dobArr[0], +dobArr[1] - 1, +dobArr[2]);
      }

      this.personalForm.patchValue({
        firstName: this.personData.firstName,
        lastName: this.personData.lastName,
        gender: this.personData.gender,
        birthDay: {
          year: birthDay ? birthDay.getFullYear() : '',
          month: birthDay ? birthDay.getMonth() + 1 : '',
          day: birthDay ? birthDay.getDate() : '',
        },
        email: this.personData.email,
        language: this.personData.preferredLang,
        password: '',
        confirmPassword: '',
        oldPassword: '',
      });

      this.firstName.setValidators(validateSingleSpaceEntries());

      this.lastName.setValidators(validateSingleSpaceEntries());

      this.password.setValidators(
        PasswordValidation.PasswordRestriction(this.personData.email, this.personData.firstName, false)
      );

      this.personalForm.setValidators([
        MustMatch('password', 'confirmPassword'),
        MustMatchIfValue('email', 'confirmEmail', this.personData.email, true),
      ]);

      this.personalForm.markAsPristine();
    }
  }

  ngOnInit() {
    this.loadUserData();
  }

  get selectedLanguage(): string {
    const lang = this.personalForm.get('language').value;
    if (lang) {
      const selected = this.languagesDescriptions.find((e) => e.code === lang);
      return selected && selected.language;
    }

    return null;
  }

  get firstName(): AbstractControl {
    return this.personalForm.get('firstName');
  }

  get lastName(): AbstractControl {
    return this.personalForm.get('lastName');
  }

  get password(): AbstractControl {
    return this.personalForm.get('password');
  }

  get confirmPassword(): AbstractControl {
    return this.personalForm.get('confirmPassword');
  }

  get oldPassword(): AbstractControl {
    return this.personalForm.get('oldPassword');
  }

  get email(): AbstractControl {
    return this.personalForm.get('email');
  }

  get confirmEmail(): AbstractControl {
    return this.personalForm.get('confirmEmail');
  }

  get isSaveDisabled() {
    return (
      this.personalForm.invalid 
      || !this.personalForm.dirty 
      || this.isRequesting 
      || !this.tokenService.hasToken 
      || this.tokenService.isLoggedAs
    );
  }

  save() {
    if (!this.isSaveDisabled) {
      this.isValidationError = false;
      this.isError = false;
      const pass = this.personalForm.get('password').value;
      const oldPass = this.personalForm.get('oldPassword').value;

      if (pass && !oldPass) {
        // If the user changed his password
        this.modalRef = this.modalService.open(this.oldPasswordTemplate);
      } else {
        this.isRequesting = true;

        const dob = this.personalForm.get('birthDay').value;
        const sendData = {
          id: this.personData.id,
          firstName: this.personalForm.get('firstName').value,
          lastName: this.personalForm.get('lastName').value,
          gender: this.personalForm.get('gender').value,
          dob: `${dob.year}-${dob.month}-${dob.day}`,
          email: this.personalForm.get('email').value,
          emailVerify: this.personalForm.get('email').value,
          preferredLang: this.personalForm.get('language').value,
          accountPassword: '',
          newPassword: '',
          oldPassword: '',
        };

        if (pass) {
          sendData['accountPassword'] = pass;
          sendData['newPassword'] = pass;
          sendData['oldPassword'] = oldPass;
        }

        this.profileService.updatePersonData(sendData).subscribe((data) => {
          if (data) {
            if (!data.error) {
              if (data.preferredLang !== this.personData.preferredLang) {
                location.reload();
              } else {
                if (this.modalRef) {
                  this.modalRef.close();
                }

                this.personalForm.markAsPristine();
                this.savedDisplayed = true;

                setTimeout(() => (this.savedDisplayed = false), 3000);
              }
            } else {
              this.isValidationError = true;
            }
          } else {
            this.isError = true;
            setTimeout(() => (this.isError = false), 3000);
          }
          this.isRequesting = false;
        });
      }
    }
  }
}



// WEBPACK FOOTER //
// ./ts/app/preferences/personal-info/personal-info.component.ts